import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Treatment } from 'src/app/core/remote-json/treatments/treatments.service';
import { EConsultFormGroup } from 'src/app/econsult/econsult-form.model';
import { defaultTreatmentTemplateMap } from '../treatment-template-model';
import {
  CreateTreatmentTemplateInput,
  TreatmentTemplate,
  UpdateTreatmentTemplateInput
} from './../../../../../API';
import { TreatmentFiltersCDSService } from './../../../../shared/consult-forms/dry-eye-forms/dry-eye-assessment-form/treatment-filters/treatment-filters-cds.service';
import { ClinicSetupService } from './../../clinic-setup.service';
import { TreatmentTemplateService } from './../treatment-template.service';

interface TreatmentTemplateValues {
  id: string;
  friendlyId: string;
  copiedTreatmentTemplateId: string;
  recommendationConditions: string[];
  treatmentTemplateClinicId: string;
  otcEyeDrops: Treatment[];
  rxEyeDrops: Treatment[];
  procedures: Treatment[];
  exercises: Treatment[];
}

interface TreatmentTemplateActionModalData {
  isView: boolean;
  action: 'add' | 'update';
  treatmentTemplate?: TreatmentTemplate;
}

interface RecommendationConditionOption {
  key: string;
  label: string;
}

@Component({
  selector: 'csi-treatment-template-actions-modal',
  templateUrl: './treatment-template-actions-modal.component.html',
  styleUrls: ['./treatment-template-actions-modal.component.css'],
  providers: [TreatmentFiltersCDSService, EConsultFormGroup]
})
export class TreatmentTemplateActionsModalComponent implements OnInit {
  public isLoading: boolean;
  public treatmentIdControl = new FormControl(null, [
    Validators.required,
    Validators.pattern(/^[a-zA-Z0-9\-\_\.]{1,15}$/),
    this.treatmentTemplateIdValidator()
  ]);

  public exercisesControl = new FormControl();
  public otcEyeDropsControl = new FormControl();
  public rxEyeDropsControl = new FormControl();
  public proceduresControl = new FormControl();
  public recommendationConditionsControl = new FormControl([]);
  public recommendationConditionsOptions: RecommendationConditionOption[] = Object.entries(
    defaultTreatmentTemplateMap || {}
  ).map(([key, template]) => ({
    key,
    label: template.friendlyId || key
  }));

  private recommendationConditionOptionKeySet = new Set(
    this.recommendationConditionsOptions.map(option => option.key)
  );

  public treatmentTemplateFormGroup = new FormGroup({
    id: new FormControl(),
    copiedTreatmentTemplateId: new FormControl(),
    recommendationConditions: this.recommendationConditionsControl,
    friendlyId: this.treatmentIdControl,
    otcEyeDrops: this.otcEyeDropsControl,
    rxEyeDrops: this.rxEyeDropsControl,
    procedures: this.proceduresControl,
    exercises: this.exercisesControl
  });

  constructor(
    public treatmentFiltersCDSService: TreatmentFiltersCDSService,
    private treatmentTemplateService: TreatmentTemplateService,
    private clinicSetupService: ClinicSetupService,
    private dialogRef: MatDialogRef<TreatmentTemplateActionsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TreatmentTemplateActionModalData
  ) {}

  ngOnInit() {
    if (this.data.treatmentTemplate) {
      this.treatmentTemplateFormGroup.patchValue(this.data.treatmentTemplate);

      if (this.data.action === 'add') {
        this.treatmentTemplateFormGroup.controls.copiedTreatmentTemplateId.setValue(
          this.data.treatmentTemplate.id
        );
        [
          this.treatmentTemplateFormGroup.controls.friendlyId,
          this.treatmentTemplateFormGroup.controls.id
        ].forEach(control => control.setValue(null));
      }
    }

    this.initializeRecommendationConditions();

    if (this.data.isView) {
      this.treatmentIdControl.disable();
      this.exercisesControl.disable();
      this.otcEyeDropsControl.disable();
      this.rxEyeDropsControl.disable();
      this.proceduresControl.disable();
    }
  }

  addTreatmentTemplate() {
    if (this.treatmentTemplateFormGroup.valid) {
      this.isLoading = true;
      this.treatmentTemplateService
        .addTreatmentTemplate(
          this.parseTreatmentTemplateMutationInput({
            ...this.treatmentTemplateFormGroup.value,
            treatmentTemplateClinicId: this.clinicSetupService.clinicId
          }) as CreateTreatmentTemplateInput
        )
        .subscribe(addedTreatmentTemplate => {
          this.treatmentTemplateService.addToTreatmentTemplateControl(addedTreatmentTemplate);
          this.isLoading = false;
          this.dialogRef.close(addedTreatmentTemplate);
        });
    }
  }

  updateTreatmentTemplate() {
    if (this.treatmentTemplateFormGroup.valid) {
      this.isLoading = true;
      this.treatmentTemplateService
        .updateTreatmentTemplate(
          this.parseTreatmentTemplateMutationInput({
            ...this.treatmentTemplateFormGroup.value,
            treatmentTemplateClinicId: this.clinicSetupService.clinicId
          }) as UpdateTreatmentTemplateInput
        )
        .subscribe(updatedTreatmentTemplate => {
          this.treatmentTemplateService.updateTreatmentTemplateControl(updatedTreatmentTemplate);
          this.isLoading = false;
          this.dialogRef.close(updatedTreatmentTemplate);
        });
    }
  }

  parseTreatmentTemplateMutationInput({
    id,
    friendlyId,
    treatmentTemplateClinicId,
    copiedTreatmentTemplateId,
    recommendationConditions,
    ...treatmentFormControls
  }: TreatmentTemplateValues): CreateTreatmentTemplateInput | UpdateTreatmentTemplateInput {
    const treatmentTemplateMutationInput:
      | CreateTreatmentTemplateInput
      | UpdateTreatmentTemplateInput = {
      friendlyId,
      treatmentTemplateClinicId,
      copiedTreatmentTemplateId,
      id
    };

    if (Array.isArray(recommendationConditions)) {
      treatmentTemplateMutationInput.recommendationConditions = recommendationConditions
        .map(value => (value ? String(value) : ''))
        .filter(value => !!value);
    }

    Object.entries(treatmentFormControls).forEach(([key, values]: [string, Treatment[]]) => {
      if (values) {
        treatmentTemplateMutationInput[key] = values.map(
          value => value.id || ((value as any) as string)
        );
      }
    });

    return treatmentTemplateMutationInput;
  }

  private treatmentTemplateIdValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (
        !this.treatmentTemplateService.treatmentTemplatesFormControl.value ||
        this.treatmentTemplateService.treatmentTemplatesFormControl.value.length <= 0
      ) {
        return null;
      }

      if (
        this.treatmentTemplateService.treatmentTemplatesFormControl.value.some(
          (treatmentTemplate: TreatmentTemplate) =>
            treatmentTemplate.friendlyId === control.value &&
            treatmentTemplate.id !== this.treatmentTemplateFormGroup.controls.id.value
        )
      ) {
        return { duplicate: true };
      }
      return null;
    };
  }

  private initializeRecommendationConditions(): void {
    const existing = this.normalizeRecommendationConditions(
      this.recommendationConditionsControl.value
    );

    if (existing.length > 0) {
      this.recommendationConditionsControl.setValue(existing);
      return;
    }

    const autoFilled = this.getAutoRecommendationConditions();
    if (autoFilled.length > 0) {
      this.recommendationConditionsControl.setValue(autoFilled);
    }
  }

  private getAutoRecommendationConditions(): string[] {
    const sourceTemplate = this.data.treatmentTemplate;
    if (!sourceTemplate) return [];

    const fromSource = this.normalizeRecommendationConditions(sourceTemplate.recommendationConditions);
    if (fromSource.length > 0) return fromSource;

    const sourceKey = this.resolveDefaultRecommendationKey(sourceTemplate);
    return sourceKey ? [sourceKey] : [];
  }

  private resolveDefaultRecommendationKey(template: TreatmentTemplate): string {
    const normalizedId = this.normalizeValue(template && template.id);
    const normalizedFriendlyId = this.normalizeValue(template && template.friendlyId);
    const normalizedCopiedId = this.normalizeValue(template && template.copiedTreatmentTemplateId);

    if (normalizedCopiedId && this.recommendationConditionOptionKeySet.has(normalizedCopiedId)) {
      return normalizedCopiedId;
    }

    if (normalizedFriendlyId && this.recommendationConditionOptionKeySet.has(normalizedFriendlyId)) {
      return normalizedFriendlyId;
    }

    if (normalizedId && this.recommendationConditionOptionKeySet.has(normalizedId)) {
      return normalizedId;
    }

    const exactRefMatch = Object.entries(defaultTreatmentTemplateMap || {}).find(
      ([, defaultTemplate]) => defaultTemplate === template
    );
    if (exactRefMatch) return exactRefMatch[0];

    const valueMatch = Object.entries(defaultTreatmentTemplateMap || {}).find(([key, defaultTemplate]) =>
      [key, defaultTemplate.id, defaultTemplate.friendlyId]
        .map(value => this.normalizeValue(value))
        .some(
          value =>
            !!value &&
            [normalizedId, normalizedFriendlyId, normalizedCopiedId].includes(value)
        )
    );

    return valueMatch ? valueMatch[0] : '';
  }

  private normalizeRecommendationConditions(values: any): string[] {
    return (values || [])
      .map(value => this.normalizeValue(value))
      .filter(
        (value, index, array) =>
          !!value && this.recommendationConditionOptionKeySet.has(value) && array.indexOf(value) === index
      );
  }

  private normalizeValue(value: any): string {
    return value ? String(value).trim() : '';
  }
}
